import { SvgIcon, SvgIconProps } from '@mui/material'
import React from 'react'

/*
    Enion appliance icons (v5, 2026).

    Drawn on a 48 x 48 grid with a 2-unit stroke. The appliance is drawn in
    currentColor; one element per icon carries the "energy" accent.

    Colours can be tuned with CSS custom properties on any ancestor:
      --enion-icon-cut     fill behind outlines, should match the surface (default: Grafiitti #1A2026)
      --enion-icon-accent  accent colour for the energy element (default: Violetti #7E4AEB)
*/

const CUT = 'var(--enion-icon-cut, #1A2026)'
const ACCENT = 'var(--enion-icon-accent, #7E4AEB)'
const SUN = '#F4E356'

const o: React.CSSProperties = { fill: CUT, stroke: 'currentColor', strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' }
const line: React.CSSProperties = { fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' }
const solid: React.CSSProperties = { fill: 'currentColor', stroke: 'none' }
const accent: React.CSSProperties = { fill: 'none', stroke: ACCENT, strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' }
const accentDashed: React.CSSProperties = { ...accent, strokeDasharray: '2.4 2.8' }
const accentFill: React.CSSProperties = { fill: ACCENT, stroke: 'none' }
const sun: React.CSSProperties = { fill: SUN, stroke: 'none' }
const label: React.CSSProperties = { fill: 'currentColor', stroke: 'none', fontSize: 7.4, fontWeight: 600, fontFamily: 'inherit', textTransform: 'none' }

/** Electricity meter: dial with needle and kWh label. */
export function EnergyMeterIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <rect style={o} x={6} y={6} width={36} height={36} rx={7}/>
      <path style={line} d="M14.8 26.6 A9.8 9.8 0 1 1 33.2 26.6"/>
      <path style={accent} d="M14.8 26.6 A9.8 9.8 0 0 1 22.3 13.4"/>
      <path style={line} d="M24 23.5 L28.6 16.6"/>
      <circle style={solid} cx={24} cy={23.5} r={2}/>
      <text style={label} x={24} y={36.6} textAnchor="middle">kWh</text>
    </SvgIcon>
  )
}

/** EV charger: wall box and car, the cable carries the accent. */
export function EvChargerIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <path style={accent} d="M8.5 31 V35 C8.5 38.6 11 39.6 13.6 38 C15.2 37 15.8 35 16.2 32.5"/>
      <rect style={o} x={4} y={9} width={9} height={22} rx={3}/>
      <circle style={solid} cx={8.5} cy={15} r={1.3}/>
      <path style={o} d="M16 33.5 V28 C16 26.6 17 25.6 18.5 25.2 L21.8 24.5 L25.3 19.4 C25.9 18.6 26.8 18.1 27.8 18.1 H36.4 C37.5 18.1 38.4 18.6 38.9 19.5 L42 24.5 C44 25.1 45 26.3 45 28.2 V33.5 Z"/>
      <path style={line} d="M31.6 18.6 V24.5"/>
      <circle style={o} cx={22.5} cy={34} r={3.4}/>
      <circle style={o} cx={38.5} cy={34} r={3.4}/>
    </SvgIcon>
  )
}

/** Radiator with rising heat waves. */
export function RadiatorIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <path style={accent} d="M15 15 c-2 -1.8 2 -3.3 0 -5.1 c-2 -1.8 2 -3.3 0 -5.1"/>
      <path style={accent} d="M24 15 c-2 -1.8 2 -3.3 0 -5.1 c-2 -1.8 2 -3.3 0 -5.1"/>
      <path style={accent} d="M33 15 c-2 -1.8 2 -3.3 0 -5.1 c-2 -1.8 2 -3.3 0 -5.1"/>
      <rect style={o} x={4} y={19} width={40} height={19} rx={5}/>
      <path style={line} d="M11 23.5 V33.5 M16.4 23.5 V33.5 M21.8 23.5 V33.5 M27.2 23.5 V33.5 M32.6 23.5 V33.5 M38 23.5 V33.5"/>
      <path style={line} d="M11 38 V42 M37 38 V42"/>
    </SvgIcon>
  )
}

/** Air-air heat pump: outdoor unit with fan, air badge. */
export function AirAirHeatPumpIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <rect style={o} x={4} y={8} width={38} height={26} rx={5}/>
      <circle style={line} cx={17} cy={21} r={9.5}/>
      <path style={solid} transform="rotate(0 17 21)" d="M17 19.368 C15.776 15.9 17.306 12.84 20.876 12.533999999999999 C22.1 15.594000000000001 20.978 18.348 18.632 19.878 Z"/>
      <path style={solid} transform="rotate(120 17 21)" d="M17 19.368 C15.776 15.9 17.306 12.84 20.876 12.533999999999999 C22.1 15.594000000000001 20.978 18.348 18.632 19.878 Z"/>
      <path style={solid} transform="rotate(240 17 21)" d="M17 19.368 C15.776 15.9 17.306 12.84 20.876 12.533999999999999 C22.1 15.594000000000001 20.978 18.348 18.632 19.878 Z"/>
      <circle style={solid} cx={17} cy={21} r={1.7}/>
      <path style={line} d="M32 15 H37 M32 20 H37 M32 25 H37"/>
      <path style={line} d="M9 34 V38"/>
      <circle style={o} cx={37} cy={37} r={8}/>
      <path style={accent} d="M32.2 34.6 H38.4 A2 2 0 1 0 36.4 32.6 M32.2 39 H40.6 A2 2 0 1 1 38.6 41"/>
    </SvgIcon>
  )
}

/** Air-water heat pump: outdoor unit with fan, water badge. */
export function AirWaterHeatPumpIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <rect style={o} x={4} y={8} width={38} height={26} rx={5}/>
      <circle style={line} cx={17} cy={21} r={9.5}/>
      <path style={solid} transform="rotate(0 17 21)" d="M17 19.368 C15.776 15.9 17.306 12.84 20.876 12.533999999999999 C22.1 15.594000000000001 20.978 18.348 18.632 19.878 Z"/>
      <path style={solid} transform="rotate(120 17 21)" d="M17 19.368 C15.776 15.9 17.306 12.84 20.876 12.533999999999999 C22.1 15.594000000000001 20.978 18.348 18.632 19.878 Z"/>
      <path style={solid} transform="rotate(240 17 21)" d="M17 19.368 C15.776 15.9 17.306 12.84 20.876 12.533999999999999 C22.1 15.594000000000001 20.978 18.348 18.632 19.878 Z"/>
      <circle style={solid} cx={17} cy={21} r={1.7}/>
      <path style={line} d="M32 15 H37 M32 20 H37 M32 25 H37"/>
      <path style={line} d="M9 34 V38"/>
      <circle style={o} cx={37} cy={37} r={8}/>
      <path style={accentFill} d="M37 31.6 C37 31.6 33.4 35.8 33.4 38.2 A3.6 3.6 0 0 0 40.6 38.2 C40.6 35.8 37 31.6 37 31.6 Z"/>
    </SvgIcon>
  )
}

/** Ground source heat pump: indoor unit above the ground loop. */
export function GroundSourceHeatPumpIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <rect style={o} x={13} y={4} width={22} height={25} rx={5}/>
      <rect style={line} x={18} y={9} width={12} height={4.5} rx={1.5}/>
      <path style={line} d="M4 34 H44"/>
      <path style={accent} d="M19 29 V40 A3.5 3.5 0 0 0 22.5 43.5 H25.5 A3.5 3.5 0 0 0 29 40 V29"/>
    </SvgIcon>
  )
}

/** Domestic hot water heater: tank feeding a shower. */
export function BoilerIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <path style={line} d="M14 8.5 V6.5 A2.5 2.5 0 0 1 16.5 4 H35 A2.5 2.5 0 0 1 37.5 6.5 V9"/>
      <path style={o} d="M4 15 C4 10.6 8 8.5 14 8.5 C20 8.5 24 10.6 24 15 V40 A4 4 0 0 1 20 44 H8 A4 4 0 0 1 4 40 Z"/>
      <path style={line} d="M14 21.5 C14 21.5 9.2 27.2 9.2 30.8 A4.8 4.8 0 0 0 18.8 30.8 C18.8 27.2 14 21.5 14 21.5 Z"/>
      <path style={o} d="M30.5 15.5 A7 6.5 0 0 1 44.5 15.5 Z"/>
      <circle style={accentFill} cx={33} cy={21} r={1.6}/>
      <circle style={accentFill} cx={37.5} cy={21} r={1.6}/>
      <circle style={accentFill} cx={42} cy={21} r={1.6}/>
      <circle style={accentFill} cx={35.2} cy={26.5} r={1.6}/>
      <circle style={accentFill} cx={39.8} cy={26.5} r={1.6}/>
      <circle style={accentFill} cx={33} cy={32} r={1.6}/>
      <circle style={accentFill} cx={37.5} cy={32} r={1.6}/>
      <circle style={accentFill} cx={42} cy={32} r={1.6}/>
    </SvgIcon>
  )
}

/** In-floor heating: house with a heating loop in the base slab. */
export function InFloorHeatingIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <path style={line} d="M8 32 V19.5 L24 6 L40 19.5 V32"/>
      <rect style={o} x={4} y={32} width={40} height={11} rx={4}/>
      <path style={accent} d="M8 37.5 c1.85 -2.8 3.7 -2.8 5.5 0 s3.7 2.8 5.5 0 s3.7 -2.8 5.5 0 s3.7 2.8 5.5 0 s3.7 -2.8 5.5 0 s3.7 2.8 5.5 0"/>
    </SvgIcon>
  )
}

/** Heating storage tank: tank feeding a radiator. */
export function StorageTankIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <path style={line} d="M24 26 H30 M24 37 H30"/>
      <path style={o} d="M4 15 C4 10.6 8 8.5 14 8.5 C20 8.5 24 10.6 24 15 V40 A4 4 0 0 1 20 44 H8 A4 4 0 0 1 4 40 Z"/>
      <path style={line} d="M14 21.5 C14 21.5 9.2 27.2 9.2 30.8 A4.8 4.8 0 0 0 18.8 30.8 C18.8 27.2 14 21.5 14 21.5 Z"/>
      <path style={accent} d="M33 20 c-1.8 -1.6 1.8 -3 0 -4.6 c-1.8 -1.6 1.8 -3 0 -4.6"/>
      <path style={accent} d="M38.5 20 c-1.8 -1.6 1.8 -3 0 -4.6 c-1.8 -1.6 1.8 -3 0 -4.6"/>
      <rect style={o} x={29} y={23.5} width={15} height={16} rx={3.5}/>
      <path style={line} d="M33.5 27.5 V35.5 M36.5 27.5 V35.5 M39.5 27.5 V35.5"/>
    </SvgIcon>
  )
}

/** Solar inverter: panel and sun. */
export function SolarPanelIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <circle style={sun} cx={35.5} cy={11} r={6}/>
      <rect style={o} x={4} y={15} width={34} height={22} rx={4}/>
      <path style={line} d="M15.3 15 V37 M26.7 15 V37 M4 26 H38"/>
      <path style={line} d="M21 37 V42.5 M14 42.5 H28"/>
    </SvgIcon>
  )
}

/** Reserve market battery: battery with grid-frequency badge. */
export function FcrBatteryIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <rect style={o} x={15} y={4} width={8} height={5} rx={1.5}/>
      <rect style={o} x={8} y={7.5} width={22} height={36.5} rx={5}/>
      <rect style={accentFill} x={12.5} y={20} width={13} height={5.2} rx={1.6}/>
      <rect style={accentFill} x={12.5} y={27.6} width={13} height={5.2} rx={1.6}/>
      <rect style={accentFill} x={12.5} y={35.2} width={13} height={5.2} rx={1.6}/>
      <circle style={o} cx={37} cy={37} r={8}/>
      <path style={line} d="M31.4 37 C32.8 33.4 35.2 33.4 37 37 S41.2 40.6 42.6 37"/>
    </SvgIcon>
  )
}

/** Battery or hybrid inverter. */
export function BatteryIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <rect style={o} x={20} y={4} width={8} height={5} rx={1.5}/>
      <rect style={o} x={13} y={7.5} width={22} height={36.5} rx={5}/>
      <rect style={accentFill} x={17.5} y={20} width={13} height={5.2} rx={1.6}/>
      <rect style={accentFill} x={17.5} y={27.6} width={13} height={5.2} rx={1.6}/>
      <rect style={accentFill} x={17.5} y={35.2} width={13} height={5.2} rx={1.6}/>
    </SvgIcon>
  )
}

/** Other relay: contactor front panel with its circuit symbol. */
export function OtherRelayIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <rect style={o} x={5} y={4} width={38} height={40} rx={6}/>
      <path style={line} d="M15 9 V17.5 M15 30.5 V39 M33 9 V17.5 M30.5 17.5 H33 M33 31 V39"/>
      <rect style={line} x={10} y={17.5} width={10} height={13} rx={1.8}/>
      <path style={accentDashed} d="M20 24 H29.6"/>
      <path style={line} d="M33 31 L27.8 19.6"/>
      <circle style={solid} cx={15} cy={9} r={1.8}/>
      <circle style={solid} cx={33} cy={9} r={1.8}/>
      <circle style={solid} cx={15} cy={39} r={1.8}/>
      <circle style={solid} cx={33} cy={39} r={1.8}/>
    </SvgIcon>
  )
}

/** Temperature meter: thermometer. */
export function TemperatureIcon(props: SvgIconProps): React.ReactElement {
  return (
    <SvgIcon viewBox="0 0 48 48" {...props}>
      <path style={o} d="M18.5 28.6 V10.5 A5.5 5.5 0 0 1 29.5 10.5 V28.6 A9.2 9.2 0 1 1 18.5 28.6 Z"/>
      <circle style={accentFill} cx={24} cy={35.4} r={4.6}/>
      <rect style={accentFill} x={22.5} y={17.5} width={3} height={17} rx={1.5}/>
      <path style={line} d="M34.5 12 H38.5 M34.5 18 H37 M34.5 24 H38.5"/>
    </SvgIcon>
  )
}
